import { AfterViewInit, Directive, ElementRef, Injector, OnInit } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, Router } from '@angular/router';
import { CrudItemComponent, UserStorage, requiredValidator } from '@scisys-uim/uim-common';
import { ASSET_TYPE_TYPE, AssetType, AssetsUser, RefCode, RequirementRule } from 'assets-model';
import { AssetTypeService, RefCodeStorage } from 'assets-service';


@Directive()
export abstract class AssetTypeItemComponentBase extends CrudItemComponent<AssetType, number> implements OnInit, AfterViewInit {

  protected systemConfig: any;
  
  public form: FormGroup;
  
  electricalAspectRequirementSelectionList: RefCode[];

  havsRagStatusSelectionList: RefCode[];

  maintenanceAspectRequirementSelectionList: RefCode[];

  maintenanceTypeSelectionList: RefCode[];

  mechanicalAspectRequirementSelectionList: RefCode[];

  wbvRagStatusSelectionList: RefCode[];


  get eAssetsUser(): AssetsUser {
    return AssetsUser.assignAs(this.userDetails);
  }

  constructor(protected assetTypeService: AssetTypeService,
    protected refCodeStorage: RefCodeStorage,
    protected fb: FormBuilder,
    protected elementRef: ElementRef,
    protected router: Router,
    protected route: ActivatedRoute,
    protected userStorage: UserStorage,
    protected dialog: MatDialog,
    protected injector: Injector,
    protected snackbar: MatSnackBar) {
    super(ASSET_TYPE_TYPE, fb, elementRef, router, route, userStorage, dialog, assetTypeService, injector, snackbar);
  }

  createNewItem() {
    return new AssetType();
  }
  
  createForm(): FormGroup {
    return this.fb.group({
      assetTypeId: [null, []],
      comments: [null, []],
      createdBy: [null, []],
      createdDate: [null, []],
      electricalAspectRequirement: [null, [requiredValidator,]],
      guidanceText: [null, []],
      havs: [null, [requiredValidator,]],
      havsRagStatus: [null, []],
      lockVersion: [null, []],
      maintenanceAspectRequirement: [null, [requiredValidator,]],
      maintenanceType: [null, []],
      mechanicalAspectRequirement: [null, [requiredValidator,]],
      name: [null, [requiredValidator,]],
      periodicMaintenance: [null, [requiredValidator,]],
      periodicMaintenanceDays: [null, []],
      portableApplianceTestingDays: [null, []],
      routineMaintenance: [null, [requiredValidator,]],
      routineMaintenanceDays: [null, []],
      transferOnApproval: [null, [requiredValidator,]],
      updatedBy: [null, []],
      updatedDate: [null, []],
      visualInspection: [null, [requiredValidator,]],
      wbv: [null, [requiredValidator,]],
      wbvRagStatus: [null, []]
    });
  }

  ngAfterViewInit(): void {
    super.ngAfterViewInit();
  }
  
  protected onInitAsyncComplete() {
    super.onInitAsyncComplete();
  }
  
  protected initSystemConfig(config: any): any {
    config = config || {};
    return config;
  }

  protected initConfig(config: any) {
    super.initConfig(config);
    this.systemConfig = this.initSystemConfig(config);
  }
  
  setDefaults() {
    if (!this.readOnly && this.createMode) {
      // re-trigger updating drop-downs.
      this.itemSet();
    }
    this.setDefaultDisabledFields();
  }

  setDefaultDisabledFields() {
    if (!this.form) {
      return;
    }
    if (!this.createMode) {
      this.assetTypeId?.disable();
    }
  }
  
  assetTypeIdChanging(value: number, unsetDependents: boolean = true) {}
  
  assetTypeIdChanged(value: number, unsetDependents: boolean = true) {
    const isUserChangeAfterPageLoad: boolean = !this.readOnly && this.isLifecycleStateItemSetComplete();
    if (isUserChangeAfterPageLoad) {
      this.assetTypeIdChangedByUser(value, unsetDependents);
    }
  }  
  
  assetTypeIdChangedByUser(value: number, unsetDependents: boolean = true) {
  }
  
  commentsChanging(value: string, unsetDependents: boolean = true) {}
  
  commentsChanged(value: string, unsetDependents: boolean = true) {
    const isUserChangeAfterPageLoad: boolean = !this.readOnly && this.isLifecycleStateItemSetComplete();
    if (isUserChangeAfterPageLoad) {
      this.commentsChangedByUser(value, unsetDependents);
    }
  }  
  
  commentsChangedByUser(value: string, unsetDependents: boolean = true) {
  }
  
  createdByChanging(value: string, unsetDependents: boolean = true) {}
  
  createdByChanged(value: string, unsetDependents: boolean = true) {
    const isUserChangeAfterPageLoad: boolean = !this.readOnly && this.isLifecycleStateItemSetComplete();
    if (isUserChangeAfterPageLoad) {
      this.createdByChangedByUser(value, unsetDependents);
    }
  }  
  
  createdByChangedByUser(value: string, unsetDependents: boolean = true) {
  }
  
  createdDateChanging(value: Date, unsetDependents: boolean = true) {}
  
  createdDateChanged(value: Date, unsetDependents: boolean = true) {
    const isUserChangeAfterPageLoad: boolean = !this.readOnly && this.isLifecycleStateItemSetComplete();
    if (isUserChangeAfterPageLoad) {
      this.createdDateChangedByUser(value, unsetDependents);
    }
  }  
  
  createdDateChangedByUser(value: Date, unsetDependents: boolean = true) {
  }
  
  electricalAspectRequirementLoadSelectionList() {
    this.refCodeStorage.getDomainElements('REQUIREMENT_RULE').subscribe(
      data => {
        this.electricalAspectRequirementSelectionList = data;
      }
    );
  }

  electricalAspectRequirementChanging(value: RequirementRule, unsetDependents: boolean = true) {}
  
  electricalAspectRequirementChanged(value: RequirementRule, unsetDependents: boolean = true) {
    const isUserChangeAfterPageLoad: boolean = !this.readOnly && this.isLifecycleStateItemSetComplete();
    if (isUserChangeAfterPageLoad) {
      this.electricalAspectRequirementChangedByUser(value, unsetDependents);
    }
  }  
  
  electricalAspectRequirementChangedByUser(value: RequirementRule, unsetDependents: boolean = true) {
  }
  
  guidanceTextChanging(value: string, unsetDependents: boolean = true) {}
  
  guidanceTextChanged(value: string, unsetDependents: boolean = true) {
    const isUserChangeAfterPageLoad: boolean = !this.readOnly && this.isLifecycleStateItemSetComplete();
    if (isUserChangeAfterPageLoad) {
      this.guidanceTextChangedByUser(value, unsetDependents);
    }
  }  
  
  guidanceTextChangedByUser(value: string, unsetDependents: boolean = true) {
  }
  
  havsChanging(value: boolean, unsetDependents: boolean = true) {}
  
  havsChanged(value: boolean, unsetDependents: boolean = true) {
    const isUserChangeAfterPageLoad: boolean = !this.readOnly && this.isLifecycleStateItemSetComplete();
    if (isUserChangeAfterPageLoad) {
      this.havsChangedByUser(value, unsetDependents);
    }
  }  
  
  havsChangedByUser(value: boolean, unsetDependents: boolean = true) {
  }
  
  havsRagStatusLoadSelectionList() {
    this.refCodeStorage.getDomainElements('RAG_STATUS').subscribe(
      data => {
        this.havsRagStatusSelectionList = data;
      }
    );
  }

  havsRagStatusChanging(value: string, unsetDependents: boolean = true) {}
  
  havsRagStatusChanged(value: string, unsetDependents: boolean = true) {
    const isUserChangeAfterPageLoad: boolean = !this.readOnly && this.isLifecycleStateItemSetComplete();
    if (isUserChangeAfterPageLoad) {
      this.havsRagStatusChangedByUser(value, unsetDependents);
    }
  }  
  
  havsRagStatusChangedByUser(value: string, unsetDependents: boolean = true) {
  }
  
  lockVersionChanging(value: number, unsetDependents: boolean = true) {}
  
  lockVersionChanged(value: number, unsetDependents: boolean = true) {
    const isUserChangeAfterPageLoad: boolean = !this.readOnly && this.isLifecycleStateItemSetComplete();
    if (isUserChangeAfterPageLoad) {
      this.lockVersionChangedByUser(value, unsetDependents);
    }
  }  
  
  lockVersionChangedByUser(value: number, unsetDependents: boolean = true) {
  }
  
  maintenanceAspectRequirementLoadSelectionList() {
    this.refCodeStorage.getDomainElements('REQUIREMENT_RULE').subscribe(
      data => {
        this.maintenanceAspectRequirementSelectionList = data;
      }
    );
  }

  maintenanceAspectRequirementChanging(value: RequirementRule, unsetDependents: boolean = true) {}
  
  maintenanceAspectRequirementChanged(value: RequirementRule, unsetDependents: boolean = true) {
    const isUserChangeAfterPageLoad: boolean = !this.readOnly && this.isLifecycleStateItemSetComplete();
    if (isUserChangeAfterPageLoad) {
      this.maintenanceAspectRequirementChangedByUser(value, unsetDependents);
    }
  }  
  
  maintenanceAspectRequirementChangedByUser(value: RequirementRule, unsetDependents: boolean = true) {
  }
  
  maintenanceTypeLoadSelectionList() {
    this.refCodeStorage.getDomainElements('MAINTENANCE_TYPE').subscribe(
      data => {
        this.maintenanceTypeSelectionList = data;
      }
    );
  }

  maintenanceTypeChanging(value: string, unsetDependents: boolean = true) {}
  
  maintenanceTypeChanged(value: string, unsetDependents: boolean = true) {
    const isUserChangeAfterPageLoad: boolean = !this.readOnly && this.isLifecycleStateItemSetComplete();
    if (isUserChangeAfterPageLoad) {
      this.maintenanceTypeChangedByUser(value, unsetDependents);
    }
  }  
  
  maintenanceTypeChangedByUser(value: string, unsetDependents: boolean = true) {
  }
  
  mechanicalAspectRequirementLoadSelectionList() {
    this.refCodeStorage.getDomainElements('REQUIREMENT_RULE').subscribe(
      data => {
        this.mechanicalAspectRequirementSelectionList = data;
      }
    );
  }

  mechanicalAspectRequirementChanging(value: RequirementRule, unsetDependents: boolean = true) {}
  
  mechanicalAspectRequirementChanged(value: RequirementRule, unsetDependents: boolean = true) {
    const isUserChangeAfterPageLoad: boolean = !this.readOnly && this.isLifecycleStateItemSetComplete();
    if (isUserChangeAfterPageLoad) {
      this.mechanicalAspectRequirementChangedByUser(value, unsetDependents);
    }
  }  
  
  mechanicalAspectRequirementChangedByUser(value: RequirementRule, unsetDependents: boolean = true) {
  }
  
  nameChanging(value: string, unsetDependents: boolean = true) {}
  
  nameChanged(value: string, unsetDependents: boolean = true) {
    const isUserChangeAfterPageLoad: boolean = !this.readOnly && this.isLifecycleStateItemSetComplete();
    if (isUserChangeAfterPageLoad) {
      this.nameChangedByUser(value, unsetDependents);
    }
  }  
  
  nameChangedByUser(value: string, unsetDependents: boolean = true) {
  }
  
  periodicMaintenanceChanging(value: boolean, unsetDependents: boolean = true) {}
  
  periodicMaintenanceChanged(value: boolean, unsetDependents: boolean = true) {
    const isUserChangeAfterPageLoad: boolean = !this.readOnly && this.isLifecycleStateItemSetComplete();
    if (isUserChangeAfterPageLoad) {
      this.periodicMaintenanceChangedByUser(value, unsetDependents);
    }
  }  
  
  periodicMaintenanceChangedByUser(value: boolean, unsetDependents: boolean = true) {
  }
  
  periodicMaintenanceDaysChanging(value: number, unsetDependents: boolean = true) {}
  
  periodicMaintenanceDaysChanged(value: number, unsetDependents: boolean = true) {
    const isUserChangeAfterPageLoad: boolean = !this.readOnly && this.isLifecycleStateItemSetComplete();
    if (isUserChangeAfterPageLoad) {
      this.periodicMaintenanceDaysChangedByUser(value, unsetDependents);
    }
  }  
  
  periodicMaintenanceDaysChangedByUser(value: number, unsetDependents: boolean = true) {
  }
  
  portableApplianceTestingDaysChanging(value: number, unsetDependents: boolean = true) {}
  
  portableApplianceTestingDaysChanged(value: number, unsetDependents: boolean = true) {
    const isUserChangeAfterPageLoad: boolean = !this.readOnly && this.isLifecycleStateItemSetComplete();
    if (isUserChangeAfterPageLoad) {
      this.portableApplianceTestingDaysChangedByUser(value, unsetDependents);
    }
  }  
  
  portableApplianceTestingDaysChangedByUser(value: number, unsetDependents: boolean = true) {
  }
  
  routineMaintenanceChanging(value: boolean, unsetDependents: boolean = true) {}
  
  routineMaintenanceChanged(value: boolean, unsetDependents: boolean = true) {
    const isUserChangeAfterPageLoad: boolean = !this.readOnly && this.isLifecycleStateItemSetComplete();
    if (isUserChangeAfterPageLoad) {
      this.routineMaintenanceChangedByUser(value, unsetDependents);
    }
  }  
  
  routineMaintenanceChangedByUser(value: boolean, unsetDependents: boolean = true) {
  }
  
  routineMaintenanceDaysChanging(value: number, unsetDependents: boolean = true) {}
  
  routineMaintenanceDaysChanged(value: number, unsetDependents: boolean = true) {
    const isUserChangeAfterPageLoad: boolean = !this.readOnly && this.isLifecycleStateItemSetComplete();
    if (isUserChangeAfterPageLoad) {
      this.routineMaintenanceDaysChangedByUser(value, unsetDependents);
    }
  }  
  
  routineMaintenanceDaysChangedByUser(value: number, unsetDependents: boolean = true) {
  }
  
  transferOnApprovalChanging(value: boolean, unsetDependents: boolean = true) {}
  
  transferOnApprovalChanged(value: boolean, unsetDependents: boolean = true) {
    const isUserChangeAfterPageLoad: boolean = !this.readOnly && this.isLifecycleStateItemSetComplete();
    if (isUserChangeAfterPageLoad) {
      this.transferOnApprovalChangedByUser(value, unsetDependents);
    }
  }  
  
  transferOnApprovalChangedByUser(value: boolean, unsetDependents: boolean = true) {
  }
  
  updatedByChanging(value: string, unsetDependents: boolean = true) {}
  
  updatedByChanged(value: string, unsetDependents: boolean = true) {
    const isUserChangeAfterPageLoad: boolean = !this.readOnly && this.isLifecycleStateItemSetComplete();
    if (isUserChangeAfterPageLoad) {
      this.updatedByChangedByUser(value, unsetDependents);
    }
  }  
  
  updatedByChangedByUser(value: string, unsetDependents: boolean = true) {
  }
  
  updatedDateChanging(value: Date, unsetDependents: boolean = true) {}
  
  updatedDateChanged(value: Date, unsetDependents: boolean = true) {
    const isUserChangeAfterPageLoad: boolean = !this.readOnly && this.isLifecycleStateItemSetComplete();
    if (isUserChangeAfterPageLoad) {
      this.updatedDateChangedByUser(value, unsetDependents);
    }
  }  
  
  updatedDateChangedByUser(value: Date, unsetDependents: boolean = true) {
  }
  
  visualInspectionChanging(value: boolean, unsetDependents: boolean = true) {}
  
  visualInspectionChanged(value: boolean, unsetDependents: boolean = true) {
    const isUserChangeAfterPageLoad: boolean = !this.readOnly && this.isLifecycleStateItemSetComplete();
    if (isUserChangeAfterPageLoad) {
      this.visualInspectionChangedByUser(value, unsetDependents);
    }
  }  
  
  visualInspectionChangedByUser(value: boolean, unsetDependents: boolean = true) {
  }
  
  wbvChanging(value: boolean, unsetDependents: boolean = true) {}
  
  wbvChanged(value: boolean, unsetDependents: boolean = true) {
    const isUserChangeAfterPageLoad: boolean = !this.readOnly && this.isLifecycleStateItemSetComplete();
    if (isUserChangeAfterPageLoad) {
      this.wbvChangedByUser(value, unsetDependents);
    }
  }  
  
  wbvChangedByUser(value: boolean, unsetDependents: boolean = true) {
  }
  
  wbvRagStatusLoadSelectionList() {
    this.refCodeStorage.getDomainElements('RAG_STATUS').subscribe(
      data => {
        this.wbvRagStatusSelectionList = data;
      }
    );
  }

  wbvRagStatusChanging(value: string, unsetDependents: boolean = true) {}
  
  wbvRagStatusChanged(value: string, unsetDependents: boolean = true) {
    const isUserChangeAfterPageLoad: boolean = !this.readOnly && this.isLifecycleStateItemSetComplete();
    if (isUserChangeAfterPageLoad) {
      this.wbvRagStatusChangedByUser(value, unsetDependents);
    }
  }  
  
  wbvRagStatusChangedByUser(value: string, unsetDependents: boolean = true) {
  }
  

  control(name: string) {
    return this.form ? this.form.get(name) : null;
  }

  get assetTypeId() {
    return this.control('assetTypeId');
  }
  
  get comments() {
    return this.control('comments');
  }
  
  get createdBy() {
    return this.control('createdBy');
  }
  
  get createdDate() {
    return this.control('createdDate');
  }
  
  get electricalAspectRequirement() {
    return this.control('electricalAspectRequirement');
  }
  
  get guidanceText() {
    return this.control('guidanceText');
  }
  
  get havs() {
    return this.control('havs');
  }
  
  get havsRagStatus() {
    return this.control('havsRagStatus');
  }
  
  get lockVersion() {
    return this.control('lockVersion');
  }
  
  get maintenanceAspectRequirement() {
    return this.control('maintenanceAspectRequirement');
  }
  
  get maintenanceType() {
    return this.control('maintenanceType');
  }
  
  get mechanicalAspectRequirement() {
    return this.control('mechanicalAspectRequirement');
  }
  
  get name() {
    return this.control('name');
  }
  
  get periodicMaintenance() {
    return this.control('periodicMaintenance');
  }
  
  get periodicMaintenanceDays() {
    return this.control('periodicMaintenanceDays');
  }
  
  get portableApplianceTestingDays() {
    return this.control('portableApplianceTestingDays');
  }
  
  get routineMaintenance() {
    return this.control('routineMaintenance');
  }
  
  get routineMaintenanceDays() {
    return this.control('routineMaintenanceDays');
  }
  
  get transferOnApproval() {
    return this.control('transferOnApproval');
  }
  
  get updatedBy() {
    return this.control('updatedBy');
  }
  
  get updatedDate() {
    return this.control('updatedDate');
  }
  
  get visualInspection() {
    return this.control('visualInspection');
  }
  
  get wbv() {
    return this.control('wbv');
  }
  
  get wbvRagStatus() {
    return this.control('wbvRagStatus');
  }
  
}





